/**
 * 计算缓存工具 - 使用 LRU 策略
 */

/**
 * LRU Cache 实现
 */
class LRUCache {
  constructor(maxSize = 100) {
    this.maxSize = maxSize
    this.cache = new Map()
  }

  get(key) {
    if (!this.cache.has(key)) {
      return undefined
    }
    
    // Move to end (most recently used)
    const value = this.cache.get(key)
    this.cache.delete(key)
    this.cache.set(key, value)
    
    return value
  }

  set(key, value) {
    // Delete if exists (to reorder)
    if (this.cache.has(key)) {
      this.cache.delete(key)
    }
    
    // Add to end
    this.cache.set(key, value)
    
    // Remove oldest if over limit
    if (this.cache.size > this.maxSize) {
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
  }

  has(key) {
    return this.cache.has(key)
  }

  clear() {
    this.cache.clear()
  }

  get size() {
    return this.cache.size
  }
}

/**
 * 创建带缓存的函数
 * @param {Function} fn - 要缓存的函数
 * @param {Object} options - 配置选项
 * @param {Function} options.keyGenerator - 生成缓存键的函数
 * @param {number} options.maxSize - 最大缓存数量
 * @param {number} options.ttl - 缓存过期时间（毫秒），0 表示永不过期
 * @returns {Function}
 */
export function createCachedFunction(fn, options = {}) {
  const {
    keyGenerator = (...args) => JSON.stringify(args),
    maxSize = 100,
    ttl = 0
  } = options

  const cache = new LRUCache(maxSize)
  const timestamps = new Map()

  return function (...args) {
    const key = keyGenerator(...args)
    
    // Check if cached and not expired
    if (cache.has(key)) {
      if (ttl > 0) {
        const timestamp = timestamps.get(key)
        if (Date.now() - timestamp > ttl) {
          // Expired, remove from cache
          cache.delete(key)
          timestamps.delete(key)
        } else {
          return cache.get(key)
        }
      } else {
        return cache.get(key)
      }
    }
    
    // Compute and cache
    const result = fn.apply(this, args)
    cache.set(key, result)
    
    if (ttl > 0) {
      timestamps.set(key, Date.now())
    }
    
    return result
  }
}

/**
 * 为对象方法创建缓存版本
 * @param {Object} obj - 对象
 * @param {string} methodName - 方法名
 * @param {Object} options - 配置选项
 */
export function cacheMethod(obj, methodName, options = {}) {
  const originalMethod = obj[methodName]
  if (typeof originalMethod !== 'function') {
    throw new Error(`${methodName} is not a function`)
  }
  
  obj[methodName] = createCachedFunction(originalMethod.bind(obj), options)
}

/**
 * 简单的 memoize 函数（向后兼容）
 * @param {Function} fn - 要缓存的函数
 * @param {Function} keyFn - 生成缓存键的函数
 * @returns {Function}
 */
export function memoize(fn, keyFn = (...args) => JSON.stringify(args)) {
  return createCachedFunction(fn, {
    keyGenerator: keyFn,
    maxSize: 100
  })
}

/**
 * 创建参数化的缓存键生成器
 * @param {Array<string|number>} paramIndices - 要包含在键中的参数索引
 * @returns {Function}
 */
export function createKeyGenerator(paramIndices = null) {
  if (!paramIndices) {
    return (...args) => JSON.stringify(args)
  }
  
  return (...args) => {
    const selectedArgs = paramIndices.map(i => args[i])
    return JSON.stringify(selectedArgs)
  }
}

/**
 * 创建基于数组内容的缓存键生成器
 * @param {Object} options - 配置选项
 * @returns {Function}
 */
export function createArrayKeyGenerator(options = {}) {
  const { includeLength = true, sampleSize = 5 } = options
  
  return (...args) => {
    const parts = []
    
    for (const arg of args) {
      if (Array.isArray(arg)) {
        if (includeLength) {
          parts.push(`len:${arg.length}`)
        }
        
        // Sample first and last few elements
        const samples = [
          ...arg.slice(0, Math.min(sampleSize, arg.length)),
          ...arg.slice(-Math.min(sampleSize, arg.length))
        ]
        parts.push(JSON.stringify(samples))
      } else {
        parts.push(JSON.stringify(arg))
      }
    }
    
    return parts.join('|')
  }
}

// Export LRUCache class
export { LRUCache }
